import { Dialog, DialogModule } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { SettingsPopupComponent } from '@cabinet/components/settings-popup/settings-popup.component';
import { TagModel } from '@core/models/tag.model';
import * as fromRoot from '@core/redux';
import { select, Store } from '@ngrx/store';
import { TagComponent } from '@ux/components/tag/tag.component';
import { map } from 'rxjs/operators';

@Component({
  selector: 'ux-popup-tag',
  templateUrl: './popup-tag.component.html',
  styleUrls: ['./popup-tag.component.less'],
  imports: [ReactiveFormsModule, CommonModule, TagComponent, DialogModule],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PopupTagComponent implements AfterViewChecked {
  @ViewChild('filterInput') filterInput: ElementRef;

  private readonly store = inject<Store<fromRoot.State>>(Store);
  dialog = inject(Dialog);

  // Input signals
  protected readonly multiple = input<boolean>(false);
  protected readonly projectId = input<string>(null);
  protected readonly value = input<string[]>([]);

  // Output signals
  protected readonly selectItem = output<TagModel>();

  protected readonly searchControl = new FormControl('');
  protected readonly selectedIndex = signal<number>(-1);
  protected readonly selectedValues = signal<string[]>([]);

  constructor() {
    // Синхронизируем value со selectedValues
    effect(() => {
      const val = this.value();
      if (val?.length) {
        this.selectedValues.set([...val]);
      }
    });
  }

  ngAfterViewChecked() {
    if (this.filterInput) {
      this.filterInput.nativeElement.focus();
    }
  }

  // Конвертируем searchControl.valueChanges в сигнал
  protected readonly searchValue = toSignal(
    this.searchControl.valueChanges.pipe(
      map(value => {
        // Сбрасываем индекс выбранного элемента при изменении поиска
        this.selectedIndex.set(-1);
        return value || '';
      })
    ),
    { initialValue: '' }
  );

  protected readonly isAdmin = toSignal(this.store.pipe(select(fromRoot.getUserCanAdmin)), { initialValue: false });

  protected readonly availableTags = toSignal(this.store.pipe(select(fromRoot.getTagEntities)), {
    initialValue: [] as TagModel[],
  });

  protected readonly filteredTags = computed(() => {
    const searchTerm = this.searchValue()?.toLowerCase() || '';
    const currentProjectId = this.projectId();
    return this.availableTags().filter(tag => {
      // Filter by search term
      const matchesSearch = tag.name.toLowerCase().includes(searchTerm);

      // Filter by project ID if provided
      const matchesProject = !currentProjectId || tag.projectId === currentProjectId;

      return matchesSearch && matchesProject;
    });
  });

  protected hasSelected = (id: string): boolean => this.selectedValues().includes(id);

  protected onSelect(item: TagModel): void {
    this.selectedValues.update(currentValues => {
      const newValues = this.multiple() ? this.toggleSelection(currentValues, item.id) : [item.id];
      this.selectItem.emit(item);
      return newValues;
    });
  }

  private toggleSelection(values: string[], id: string): string[] {
    const index = values.indexOf(id);
    return index > -1 ? values.filter((_, i) => i !== index) : [...values, id];
  }

  protected onArrowUp = (): void => this.moveSelection(-1);

  protected onArrowDown = (): void => this.moveSelection(1);

  private moveSelection(direction: number): void {
    this.selectedIndex.update(current => {
      const items = this.filteredTags();
      const newIndex = current + direction;

      if (newIndex < 0) {
        return items.length - 1;
      }
      if (newIndex >= items.length) {
        return 0;
      }
      return newIndex;
    });
  }

  protected onEnterSelect(): void {
    const index = this.selectedIndex();
    const items = this.filteredTags();

    if (index >= 0 && index < items.length) {
      this.onSelect(items[index]);
    }
  }

  protected onOpenSettings(): void {
    this.dialog.open<SettingsPopupComponent>(SettingsPopupComponent, {
      data: {
        tab: 'tag',
      },
    });
  }
}
